import * as R from "ramda";
import {
  AssessmentChartFilterLabels,
  AssessmentResult,
  FilterFunction,
  IUser,
  IUserFilter,
  IUsersAnalytics,
  UserFilter,
} from ".";

export const userNameMatches = (name: string) => (user: IUser) => user.displayName.includes(name);

export const userAccountMatches = (account: string) => (user: IUser) => user.accountId === account;

export const userTimeTakenBetween = (usersAnalytics: IUsersAnalytics | null, minTime: number, maxTime: number) => (
  user: IUser
) => {
  if (usersAnalytics === null) {
    return false;
  }
  const userAnalytics = usersAnalytics[user.accountId];
  if (!userAnalytics) {
    return false;
  }
  if (!userAnalytics.completed) {
    return false;
  }
  const timeTaken = userAnalytics.time - 1;
  return timeTaken >= minTime && timeTaken <= maxTime;
};

export const userPassedPreAssessment = (usersAnalytics: IUsersAnalytics | null) => (user: IUser) =>
  userAssessmentFilter(usersAnalytics, a => a.includes(AssessmentResult.PreAssessmentPassed), user);

export const userFailedPreAssessment = (usersAnalytics: IUsersAnalytics | null) => (user: IUser) =>
  userAssessmentFilter(
    usersAnalytics,
    a => !a.includes(AssessmentResult.PreAssessmentPassed) && a.includes(AssessmentResult.PreAssessmentFailed),
    user
  );

const isNotPreAssessment = (a: AssessmentResult[]) =>
  !a.includes(AssessmentResult.PreAssessmentPassed) &&
  !a.includes(AssessmentResult.PreAssessmentFailed) &&
  !a.includes(AssessmentResult.PreAssessmentFailed);

export const userPassedPostAssessment = (usersAnalytics: IUsersAnalytics | null) => (user: IUser) =>
  userAssessmentFilter(
    usersAnalytics,
    a =>
      a.includes(AssessmentResult.PostAssessmentPassed) &&
      !a.includes(AssessmentResult.PostAssessmentFailed) &&
      isNotPreAssessment(a),
    user
  );

export const userFailedPostAssessment = (usersAnalytics: IUsersAnalytics | null) => (user: IUser) =>
  userAssessmentFilter(
    usersAnalytics,
    a => a.includes(AssessmentResult.PostAssessmentFailed) && isNotPreAssessment(a),
    user
  );

export const userAssessmentFilter = (
  usersAnalytics: IUsersAnalytics | null,
  assessmentFilter: (assessment: AssessmentResult[]) => boolean,
  user: IUser
) => {
  if (usersAnalytics === null) {
    return false;
  }
  const userAnalytics = usersAnalytics[user.accountId];
  if (!userAnalytics) {
    return false;
  }
  if (!userAnalytics.completed) {
    return false;
  }
  const userAssessments = userAnalytics.assessments.filter(assessmentFilter);
  return userAssessments.length !== 0;
};

export const getUserAssessmentFilter = (label: AssessmentChartFilterLabels) => {
  switch (label) {
    case AssessmentChartFilterLabels.PassedPreAssessment:
      return userPassedPreAssessment;
    case AssessmentChartFilterLabels.PassedPostAssessment:
      return userPassedPostAssessment;
    case AssessmentChartFilterLabels.FailedPostAssessment:
      return userFailedPostAssessment;
    default:
      return userFailedPreAssessment;
  }
};

export const userTookTopic = (usersAnalytics: IUsersAnalytics | null) => (topic: number) => (user: IUser) => {
  if (usersAnalytics === null) {
    return false;
  }
  const userAnalytics = usersAnalytics[user.accountId];
  if (!userAnalytics) {
    return false;
  }
  if (!userAnalytics.completed) {
    return false;
  }
  return userAnalytics.teachings === topic;
};

export const updateFilters = (
  name: string,
  type: UserFilter,
  conditions: FilterFunction[],
  enabled: boolean,
  labels: string[],
  filters: IUserFilter[]
) => {
  const newUserFilter: IUserFilter = {
    conditions,
    enabled,
    labels,
    name,
    type,
  };
  for (let i = 0; i < filters.length; i++) {
    if (type === filters[i].type && name === filters[i].name) {
      const newFilters = filters;
      newFilters[i] = newUserFilter;
      return newFilters;
    }
  }
  return filters.concat(newUserFilter);
};

export const updateFilterConditions = (
  name: string,
  type: UserFilter,
  condition: FilterFunction,
  enabled: boolean,
  label: string,
  filters: IUserFilter[]
) => {
  for (let i = 0; i < filters.length; i++) {
    const filter = filters[i];
    if (type === filter.type && name === filter.name) {
      const labelExists = filter.labels.includes(label);
      const labelLenses = R.lensPath([i, "labels"]);
      const conditionLenses = R.lensPath([i, "conditions"]);
      if (labelExists) {
        if (filter.labels.length === 1) {
          return removeFilter(name, type, filters);
        }
        const labelIndex = filter.labels.indexOf(label);
        const removeFun = R.remove(labelIndex, 1);
        return R.pipe<IUserFilter[], IUserFilter[], IUserFilter[]>(
          R.over(labelLenses, removeFun),
          R.over(conditionLenses, removeFun)
        )(filters);
      }
      return R.pipe<IUserFilter[], IUserFilter[], IUserFilter[]>(
        R.over(labelLenses, R.append(label)),
        R.over(conditionLenses, R.append(condition))
      )(filters);
    }
  }
  return updateFilters(name, type, [condition], enabled, [label], filters);
};

export const removeLabelAndCondition = (name: string, type: UserFilter, index: number, filters: IUserFilter[]) => {
  for (let i = 0; i < filters.length; i++) {
    const filter = filters[i];
    if (type === filters[i].type && name === filters[i].name) {
      if (filter.labels.length === 1) {
        return removeFilter(name, type, filters);
      }
      return R.pipe<IUserFilter[], IUserFilter[], IUserFilter[]>(
        R.over(R.lensPath([i, "labels"]), R.remove(index, 1)),
        R.over(R.lensPath([i, "conditions"]), R.remove(index, 1))
      )(filters);
    }
  }
  return filters;
};

export const removeFilter = (name: string, type: UserFilter, filters: IUserFilter[]) => {
  for (let i = 0; i < filters.length; i++) {
    if (type === filters[i].type && name === filters[i].name) {
      const newFilters = filters;
      newFilters.splice(i, 1);
      return newFilters;
    }
  }
  return filters;
};

export const toggleFilter = (name: string, type: UserFilter, filters: IUserFilter[]) => {
  for (let i = 0; i < filters.length; i++) {
    if (type === filters[i].type && name === filters[i].name) {
      const newFilterState = !filters[i].enabled;
      const newFilters = filters;
      newFilters[i].enabled = newFilterState;
      return newFilters;
    }
  }
  return filters;
};

export const getSelectedUsers = (users: IUser[] | null, filters: IUserFilter[]) => {
  if (users === null) {
    return [];
  }
  const onlyEnabledFilters = filters.filter(f => f.enabled);
  if (onlyEnabledFilters.length === 0) {
    return [];
  }
  const filteredAccounts = [];
  const usersAmount = users.length;
  // Changed due to #335 for performance improvements
  for (let i = 0; i < usersAmount; i++) {
    const user = users[i];
    const fullfilsFilters = solvePrimaryFilter(user, onlyEnabledFilters);
    if (fullfilsFilters) {
      filteredAccounts.push(user.accountId);
    }
  }
  return filteredAccounts;
};

const solvePrimaryFilter = (user: IUser, filters: IUserFilter[]) => {
  for (const filter of filters) {
    if (!solveSecondaryFilter(user, filter.conditions)) {
      return false;
    }
  }
  return true;
};

const solveSecondaryFilter = (user: IUser, conditions: FilterFunction[]) => {
  for (const condition of conditions) {
    if (condition(user)) {
      return true;
    }
  }
  return false;
};
